<template>
    <div class="admin-container">
        <!-- Stats Section -->
        <div class="stats-section">
            <div class="stat-card">
                <div class="stat-icon-wrapper">
                    <i class="fas fa-money-bill-wave"></i>
                </div>
                <div class="stat-info">
                    <span class="stat-label">Total Transactions</span>
                    <span class="stat-value">{{ stats.total_transaction }}</span>
                    <div class="stat-trend">
                        <span>${{ Number(stats.total_transaction_amount).toFixed(2) }} Total Volume</span>
                    </div>
                </div>
            </div>

            <div class="stat-card">
                <div class="stat-icon-wrapper green">
                    <i class="fas fa-arrow-down"></i>
                </div>
                <div class="stat-info">
                    <span class="stat-label">Total Deposits</span>
                    <span class="stat-value">{{ stats.total_deposit_count }}</span>
                    <div class="stat-trend positive">
                        <span>${{ Number(stats.total_deposit_amount).toFixed(2) }} Total Volume</span>
                    </div>
                </div>
            </div>

            <div class="stat-card">
                <div class="stat-icon-wrapper purple">
                    <i class="fas fa-clock"></i>
                </div>
                <div class="stat-info">
                    <span class="stat-label">Pending Transactions</span>
                    <span class="stat-value">{{ stats.total_pending_count }}</span>
                    <div class="stat-trend">
                        <span>${{ Number(stats.total_pending_amount).toFixed(2) }} Total Volume</span>
                    </div>
                </div>
            </div>

            <div class="stat-card">
                <div class="stat-icon-wrapper red">
                    <i class="fas fa-times"></i>
                </div>
                <div class="stat-info">
                    <span class="stat-label">Failed Transactions</span>
                    <span class="stat-value">{{ stats.total_failed_count }}</span>
                    <div class="stat-trend negative">
                        <span>${{ Number(stats.total_failed_amount).toFixed(2) }} Total Volume</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Search and Filter Section -->
        <div class="control-section">
            <div class="search-box">
                <i class="fas fa-search search-icon"></i>
                <input v-model="searchQuery" type="text" placeholder="Search by transaction ID or username...">
            </div>

            <div class="filter-controls">
                <FilterDropdown v-model="statusFilter" label="Status" :options="statusOptions"
                    @update:modelValue="handleSearch" />

                <FilterDropdown v-model="typeFilter" label="Operation Type" :options="typeOptions"
                    @update:modelValue="handleSearch" />

                <FilterDropdown v-model="methodFilter" label="Payment Method" :options="methodOptions"
                    @update:modelValue="handleSearch" />

                <FilterDropdown v-model="sortBy" label="Sort By" :options="sortOptions"
                    @update:modelValue="handleSort" />

                <FilterDropdown v-model="itemsPerPage" label="Items Per Page" :options="pageSizeOptions"
                    @update:modelValue="handlePageSizeChange" />
            </div>
        </div>

        <div class="table-wrapper">
            <table class="transactions-table">
                <thead>
                    <tr>
                        <th>Transaction ID</th>
                        <th>User</th>
                        <th>Amount</th>
                        <th>Type</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="transaction in transactions" :key="transaction.id" class="transaction-row">
                        <td class="text-white">#{{ transaction.id }}</td>
                        <td class="user-cell">
                            <img :src="transaction.user.avatar_medium" :alt="transaction.user.user_name"
                                class="user-avatar">
                            <div class="user-info">
                                <span class="username">{{ transaction.user.user_name }}</span>
                                <span class="user-id">#{{ transaction.user.id }}</span>
                            </div>
                        </td>
                        <td class="amount" :class="transaction.operation_type.toLowerCase()">
                            {{ transaction.operation_type === 'WITHDRAW' ? '-' : '+' }}${{
                                Number(transaction.amount).toFixed(2) }}
                        </td>
                        <td>
                            <span :class="['operation-badge', transaction.operation_type.toLowerCase()]">
                                {{ transaction.operation_type }}
                            </span>
                        </td>
                        <td class="text-white">{{ transaction.balance_payment_method }}</td>
                        <td>
                            <span :class="['status-badge', transaction.operation_status.toLowerCase()]">
                                {{ transaction.operation_status }}
                            </span>
                        </td>
                        <td class="text-white">{{ formatDate(transaction.created_at) }}</td>
                        <td class="actions-cell">
                            <button class="action-btn view" @click="viewDetails(transaction)" title="View Details">
                                <i class="fas fa-eye"></i>
                            </button>
                            <button v-if="transaction.operation_status === 'PENDING'" class="action-btn approve"
                                @click="approveTransaction(transaction)" title="Approve">
                                <i class="fas fa-check"></i>
                            </button>
                            <button v-if="transaction.operation_status === 'PENDING'" class="action-btn reject"
                                @click="rejectTransaction(transaction)" title="Reject">
                                <i class="fas fa-times"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <PaginationWithInfo :current-page="pagination.currentPage" :total-pages="pagination.totalPages"
            :total-items="totalTransactions" item-label="transactions" @page-change="changePage" />

        <!-- Transaction Details Modal -->
        <div v-if="showDetailsModal" class="modal-overlay">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="text-white">Transaction Details</h5>
                    <button class="close-btn" @click="closeDetailsModal">&times;</button>
                </div>
                <div class="modal-body">
                    <!-- Main Transaction Details - Left Column -->
                    <div class="detail-section">
                        <h6 class="section-title">General Information</h6>
                    <div class="detail-group">
                        <label>Transaction ID</label>
                        <p class="text-white">#{{ selectedTransaction.id }}</p>
                    </div>
                    <div class="detail-group">
                        <label>User</label>
                        <div class="user-detail">
                                <img :src="selectedTransaction.user.avatar_medium"
                                    :alt="selectedTransaction.user.user_name" class="user-avatar">
                            <div>
                                <p class="text-white">{{ selectedTransaction.user.user_name }}</p>
                                <small class="text-gray">ID: #{{ selectedTransaction.user.id }}</small>
                            </div>
                        </div>
                    </div>
                    <div class="detail-group">
                        <label>Amount</label>
                        <p class="text-white" :class="selectedTransaction.operation_type.toLowerCase()">
                                {{ selectedTransaction.operation_type === 'WITHDRAW' ? '-' : '+' }}${{
                                    Number(selectedTransaction.amount).toFixed(2) }}
                                <span class="currency-tag">{{ selectedTransaction.currency }}</span>
                        </p>
                    </div>
                    <div class="detail-group">
                            <label>Wallet</label>
                            <p class="text-white">${{ Number(selectedTransaction.wallet).toFixed(2) }}</p>
                    </div>
                    <div class="detail-group">
                            <label>Payment Details</label>
                            <div class="badge-container">
                                <span class="detail-badge">{{ selectedTransaction.payment_method }}</span>
                                <span class="detail-badge">{{ selectedTransaction.balance_payment_method }}</span>
                            </div>
                    </div>
                    <div class="detail-group">
                            <label>Status Information</label>
                            <div class="badge-container">
                        <span :class="['status-badge', selectedTransaction.operation_status.toLowerCase()]">
                            {{ selectedTransaction.operation_status }}
                        </span>
                                <span :class="['status-badge', selectedTransaction.status.toLowerCase()]">
                                    {{ selectedTransaction.status }}
                                </span>
                            </div>
                    </div>
                    <div class="detail-group">
                        <label>Created At</label>
                        <p class="text-white">{{ formatDate(selectedTransaction.created_at) }}</p>
                    </div>
                    </div>

                    <!-- Transaction Details - Right Column -->
                    <div class="transaction-details">
                        <!-- Steam Trade Details -->
                        <div v-if="selectedTransaction.steam_trade" class="detail-section">
                            <h6 class="section-title">Steam Trade Details</h6>
                            <!-- Items Section -->
                    <div class="detail-group">
                                <label>Items to Receive</label>
                                <div class="items-grid">
                                    <div v-for="(item, index) in selectedTransaction.steam_trade.items_to_receive"
                                        :key="index" class="trade-item">
                                        <div class="item-image-container">
                                            <img :src="item.item_detail.image" :alt="item.item_detail.name">
                                            <span class="game-badge" :class="item.item_detail.game">
                                                {{ item.item_detail.game.toUpperCase() }}
                                            </span>
                    </div>
                                        <div class="item-details">
                                            <h6 class="item-name">{{ item.item_detail.name }}</h6>
                                            <span class="item-price">${{ (item.price / 1000).toFixed(2) }}</span>
                    </div>
                </div>
                                </div>
                            </div>

                            <div class="trade-info">
                                <div class="detail-group">
                                    <label>Trade Information</label>
                                    <div class="info-grid">
                                        <div class="info-item">
                                            <span class="info-label">Trade ID:</span>
                                            <span class="info-value">#{{ selectedTransaction.steam_trade.id }}</span>
                                        </div>
                                        <div class="info-item">
                                            <span class="info-label">Steam ID:</span>
                                            <span class="info-value">{{ selectedTransaction.steam_trade.steam_id
                                                }}</span>
                                        </div>
                                        <div class="info-item">
                                            <span class="info-label">Partner Account:</span>
                                            <span class="info-value">{{
                                                selectedTransaction.steam_trade.partner_accountid }}</span>
                                        </div>
                                        <div class="info-item">
                                            <span class="info-label">Trade ID:</span>
                                            <span class="info-value">{{ selectedTransaction.steam_trade.tradeid
                                                }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="detail-group">
                                    <label>Trade Status</label>
                                    <span
                                        :class="['status-badge', selectedTransaction.steam_trade.status.toLowerCase()]">
                                        {{ selectedTransaction.steam_trade.status }}
                                    </span>
                                </div>

                                <div class="detail-group">
                                    <label>Trade Link</label>
                                    <a :href="selectedTransaction.steam_trade.tradelink" target="_blank"
                                        class="trade-link">
                                        <i class="fas fa-external-link-alt"></i>
                                        Open Trade Link
                                    </a>
                                </div>

                                <div class="detail-group">
                                    <label>Dates</label>
                                    <div class="dates-grid">
                                        <div class="date-item">
                                            <span class="date-label">Created:</span>
                                            <span class="date-value">{{
                                                formatDate(selectedTransaction.steam_trade.created_at) }}</span>
                                        </div>
                                        <div class="date-item">
                                            <span class="date-label">Updated:</span>
                                            <span class="date-value">{{
                                                formatDate(selectedTransaction.steam_trade.updated_at) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Crypto Transaction Details -->
                        <div v-if="selectedTransaction.crypto_transaction" class="detail-section">
                            <h6 class="section-title">Crypto Transaction Details</h6>
                            <div class="detail-group">
                                <label>Transaction Information</label>
                                <div class="info-grid">
                                    <div class="info-item">
                                        <span class="info-label">Transaction ID:</span>
                                        <span class="info-value">#{{ selectedTransaction.crypto_transaction.id }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Order ID:</span>
                                        <span class="info-value">{{ selectedTransaction.crypto_transaction.order_id
                                            }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="info-label">Amount:</span>
                                        <span class="info-value">{{ selectedTransaction.crypto_transaction.price_amount
                                            }} {{ selectedTransaction.crypto_transaction.price_currency }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="detail-group">
                                <label>Status Information</label>
                                <div class="badge-container">
                                    <span
                                        :class="['status-badge', selectedTransaction.crypto_transaction.gateway_status.toLowerCase()]">
                                        Gateway: {{ selectedTransaction.crypto_transaction.gateway_status }}
                                    </span>
                                    <span
                                        :class="['status-badge', selectedTransaction.crypto_transaction.operation_status.toLowerCase()]">
                                        Operation: {{ selectedTransaction.crypto_transaction.operation_status }}
                                    </span>
                                </div>
                            </div>

                            <div class="detail-group">
                                <label>Payment Wallet</label>
                                <p class="text-white monospace">{{ selectedTransaction.crypto_transaction.payment_wallet
                                    }}</p>
                            </div>

                            <div class="detail-group">
                                <label>Invoice</label>
                                <a :href="selectedTransaction.crypto_transaction.invoice_url" target="_blank"
                                    class="invoice-link">
                                    View Invoice <i class="fas fa-external-link-alt"></i>
                                </a>
                            </div>

                            <div class="detail-group">
                                <label>Dates</label>
                                <div class="dates-grid">
                                    <div class="date-item">
                                        <span class="date-label">Created:</span>
                                        <span class="date-value">{{
                                            formatDate(selectedTransaction.crypto_transaction.created_at) }}</span>
                                    </div>
                                    <div class="date-item">
                                        <span class="date-label">Updated:</span>
                                        <span class="date-value">{{
                                            formatDate(selectedTransaction.crypto_transaction.updated_at) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button v-if="selectedTransaction.operation_status === 'PENDING'" class="btn-pinko btn-success"
                        @click="approveTransaction(selectedTransaction)">
                        <i class="fas fa-check"></i> Approve
                    </button>
                    <button v-if="selectedTransaction.operation_status === 'PENDING'" class="btn-pinko btn-danger"
                        @click="rejectTransaction(selectedTransaction)">
                        <i class="fas fa-times"></i> Reject
                    </button>
                    <button class="btn-pinko btn-secondary" @click="closeDetailsModal">Close</button>
                </div>
            </div>
        </div>

        <!-- Edit Transaction Modal -->
        <div v-if="showEditModal" class="modal-overlay">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="text-white">Edit Transaction</h5>
                    <button class="close-btn" @click="closeEditModal">&times;</button>
                </div>
                <div class="modal-body">
                    <form @submit.prevent="saveTransaction">
                        <div class="form-group">
                            <label>Amount</label>
                            <input v-model="editForm.amount" type="number" step="0.01" class="form-control" required>
                        </div>
                        <div class="form-group">
                            <label>Operation Type</label>
                            <select v-model="editForm.operation_type" class="form-control" required>
                                <option value="DEPOSIT">Deposit</option>
                                <option value="WITHDRAW">Withdraw</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Payment Method</label>
                            <select v-model="editForm.balance_payment_method" class="form-control" required>
                                <option value="CREDIT_CARD">Credit Card</option>
                                <option value="BANK_TRANSFER">Bank Transfer</option>
                                <option value="CRYPTO">Crypto</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Status</label>
                            <select v-model="editForm.operation_status" class="form-control" required>
                                <option value="PENDING">Pending</option>
                                <option value="COMPLETED">Completed</option>
                                <option value="FAILED">Failed</option>
                                <option value="CANCELLED">Cancelled</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Note</label>
                            <textarea v-model="editForm.note" class="form-control" rows="3"></textarea>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn-pinko btn-pink" @click="closeEditModal">Cancel</button>
                            <button type="submit" class="btn-pinko btn-pink">Save Changes</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AdminPaymentService from '@/services/admin.PaymentService.js';
import PaginationWithInfo from '@/components/shared/PaginationWithInfo.vue';
import FilterDropdown from '@/components/shared/FilterDropdown.vue';

export default {
    name: 'PaymentView',
    components: {
        PaginationWithInfo,
        FilterDropdown
    },
    data() {
        return {
            transactions: [],
            searchQuery: '',
            statusFilter: 'All',
            typeFilter: 'All',
            methodFilter: 'All',
            sortBy: 'created_at_desc',
            itemsPerPage: 10,
            totalTransactions: 0,
            loading: false,
            pagination: {
                totalPages: 0,
                currentPage: 1,
            },
            stats: {
                total_transaction: 0,
                total_transaction_amount: 0,
                total_deposit_amount: 0,
                total_deposit_count: 0,
                total_pending_amount: 0,
                total_pending_count: 0,
                total_failed_amount: 0,
                total_failed_count: 0
            },
            statusOptions: [
                { value: 'All', label: 'All Status' },
                { value: 'COMPLETED', label: 'Completed' },
                { value: 'PENDING', label: 'Pending' },
                { value: 'FAILED', label: 'Failed' },
                { value: 'REJECTED', label: 'Rejected' }
            ],
            typeOptions: [
                { value: 'All', label: 'All Operations' },
                { value: 'DEPOSIT', label: 'Deposit' },
                { value: 'WITHDRAW', label: 'Withdrawal' }
            ],
            methodOptions: [
                { value: 'All', label: 'All Methods' },
                { value: 'ITEMPAY', label: 'ItemPay' },
                { value: 'CRYPTOPAY', label: 'Crypto' }
            ],
            sortOptions: [
                { value: 'created_at_desc', label: 'Newest First' },
                { value: 'created_at_asc', label: 'Oldest First' },
                { value: 'amount_desc', label: 'Amount (High to Low)' },
                { value: 'amount_asc', label: 'Amount (Low to High)' }
            ],
            pageSizeOptions: [
                { value: 10, label: '10 per page' },
                { value: 20, label: '20 per page' },
                { value: 50, label: '50 per page' }
            ],
            showDetailsModal: false,
            selectedTransaction: null,
            showEditModal: false,
            editForm: {
                amount: '',
                operation_type: '',
                balance_payment_method: '',
                operation_status: '',
                note: ''
            }
        }
    },
    methods: {
        async fetchTransactions() {
            try {
                this.loading = true;
                const response = await AdminPaymentService.getAll({
                    search: this.searchQuery,
                    status: this.statusFilter,
                    type: this.typeFilter,
                    method: this.methodFilter,
                    sort: this.sortBy,
                    page: this.pagination.currentPage,
                    limit: this.itemsPerPage
                });

                this.transactions = response.items;
                this.totalTransactions = response.meta.total;
                this.pagination.totalPages = response.meta.totalPages;
                await this.fetchStats();
            } catch (error) {
                console.error('Error fetching transactions:', error);
            } finally {
                this.loading = false;
            }
        },
        async fetchStats() {
            try {
                const response = await AdminPaymentService.getStats({
                    method: this.methodFilter,
                    type: this.typeFilter,
                    status: this.statusFilter
                });
                if (!response) return;
                this.stats = response.stats;
            } catch (error) {
                console.error('Error fetching stats:', error);
            }
        },
        formatDate(date) {
            return new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        async handleSearch() {
            this.pagination.currentPage = 1;
            await this.fetchTransactions();
        },
        async handleSort(value) {
            this.sortBy = value;
            await this.fetchTransactions();
        },
        async changePage(page) {
            this.pagination.currentPage = page;
            await this.fetchTransactions();
        },
        async viewDetails(transaction) {
            const response = await AdminPaymentService.getById(transaction.id);
            if (!response) return;
            this.selectedTransaction = response;
            this.showDetailsModal = true;
        },
        closeDetailsModal() {
            this.showDetailsModal = false;
            this.selectedTransaction = null;
        },
        async approveTransaction(transaction) {
            try {
                await AdminPaymentService.updateStatus(transaction.id, 'COMPLETED');
                await this.fetchTransactions();
            } catch (error) {
                console.error('Error approving transaction:', error);
            }
        },
        async rejectTransaction(transaction) {
            try {
                await AdminPaymentService.updateStatus(transaction.id, 'REJECTED');
                await this.fetchTransactions();
            } catch (error) {
                console.error('Error rejecting transaction:', error);
            }
        },
        async handlePageSizeChange(size) {
            this.itemsPerPage = Number(size);
            this.pagination.currentPage = 1;
            await this.fetchTransactions();
        },
        async saveTransaction() {
            try {
                await AdminPaymentService.updateTransaction(this.selectedTransaction.id, this.editForm);
                await this.fetchTransactions();
                this.closeEditModal();
            } catch (error) {
                console.error('Error saving transaction:', error);
            }
        },
        closeEditModal() {
            this.showEditModal = false;
            this.editForm = {
                amount: '',
                operation_type: '',
                balance_payment_method: '',
                operation_status: '',
                note: ''
            };
        }
    },
    created() {
        this.fetchTransactions();
    },
    watch: {
        searchQuery: {
            handler: debounce(function () {
                this.handleSearch();
            }, 300),
            immediate: false
        }
    }
}

function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}
</script>

<style scoped>
.admin-container {
    padding: 2rem;
    background: #1a1a1a;
    min-height: 100vh;
}

.stats-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2.5rem;
}

.stat-card {
    background: linear-gradient(145deg, #222, #2a2a2a);
    border-radius: 16px;
    padding: 1.5rem;
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;
}

.stat-card:hover {
    transform: translateY(-2px);
}

.stat-icon-wrapper {
    width: 48px;
    height: 48px;
    background: #2563eb;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    color: white;
}

.stat-icon-wrapper.green {
    background: #059669;
}

.stat-icon-wrapper.red {
    background: #dc2626;
}

.stat-icon-wrapper.purple {
    background: #7c3aed;
}

.stat-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.stat-label {
    color: #999;
    font-size: 0.875rem;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

.stat-value {
    font-size: 2rem;
    font-weight: 600;
    color: #fff;
    line-height: 1.2;
}

.stat-trend {
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 4px 8px;
    border-radius: 6px;
    width: fit-content;
}

.stat-trend.positive {
    color: #059669;
    background: rgba(5, 150, 105, 0.1);
}

.stat-trend.negative {
    color: #dc2626;
    background: rgba(220, 38, 38, 0.1);
}

/* Table Styles */
.table-wrapper {
    background: #222;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 2rem;
    min-height: 400px;
}

.transactions-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.transactions-table th {
    background: #2a2a2a;
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: #fff;
}

.transactions-table td {
    padding: 1rem;
    border-bottom: 1px solid #333;
    color: #fff;
}

.transaction-row {
    transition: background-color 0.2s ease;
}

.transaction-row:hover {
    background: #2a2a2a;
}

.user-cell {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    object-fit: cover;
}

.user-info {
    display: flex;
    flex-direction: column;
}

.username {
    font-weight: 500;
    color: #fff;
}

.user-id {
    color: #666;
    font-size: 0.875rem;
}

.amount {
    font-weight: 600;
    font-family: monospace;
}

.amount.deposit {
    color: #059669;
}

.amount.withdrawal {
    color: #dc2626;
}

.operation-badge,
.status-badge {
    padding: 0.4rem 0.8rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    display: inline-block;
}

.operation-badge.deposit {
    background: rgba(5, 150, 105, 0.2);
    color: #059669;
}

.operation-badge.withdrawal {
    background: rgba(220, 38, 38, 0.2);
    color: #dc2626;
}

.status-badge.completed {
    background: rgba(5, 150, 105, 0.2);
    color: #059669;
}

.status-badge.pending {
    background: rgba(234, 179, 8, 0.2);
    color: #eab308;
}

.status-badge.failed {
    background: rgba(220, 38, 38, 0.2);
    color: #dc2626;
}

.actions-cell {
    display: flex;
    gap: 0.5rem;
}

.action-btn {
    padding: 0.5rem;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: #666;
    cursor: pointer;
    transition: all 0.2s ease;
}

.action-btn:hover {
    transform: translateY(-1px);
}

.action-btn.view:hover {
    color: #2563eb;
}

.action-btn.approve:hover {
    color: #059669;
}

.action-btn.reject:hover {
    color: #dc2626;
}

/* Control Section Styles */
.control-section {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1.5rem;
    margin-bottom: 2rem;
    background: linear-gradient(145deg, #1a1a1a, #222);
    border-radius: 16px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.search-box {
    display: flex;
    align-items: center;
    min-width: 300px;
    flex: 1;
    max-width: 600px;
    background: rgba(26, 26, 26, 0.8);
    border-radius: 12px;
    border: 2px solid #333;
    padding: 0.5rem 1rem;
    transition: all 0.2s ease;
}

.search-box:focus-within {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.search-icon {
    color: #666;
    margin-right: 0.75rem;
    transition: color 0.2s ease;
}

.search-box:focus-within .search-icon {
    color: #2563eb;
}

.search-box input {
    flex: 1;
    background: transparent;
    border: none;
    color: #fff;
    font-size: 1rem;
    padding: 0.5rem 0;
}

.search-box input:focus {
    outline: none;
}

.search-box input::placeholder {
    color: #666;
}

.filter-controls {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 180px;
}

.filter-group label {
    font-size: 0.875rem;
    color: #999;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

.filter-group select {
    padding: 0.875rem 2.5rem 0.875rem 1rem;
    border-radius: 12px;
    border: 2px solid #333;
    background: rgba(26, 26, 26, 0.8);
    color: #fff;
    font-size: 0.95rem;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 1em;
    transition: all 0.2s ease;
    cursor: pointer;
}

.filter-group select:focus {
    border-color: #2563eb;
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

/* Responsive Design */
@media (max-width: 1200px) {
    .stats-section {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .stats-section {
        grid-template-columns: 1fr;
    }

    .control-section {
        flex-direction: column;
    }

    .filter-controls {
        flex-direction: column;
        width: 100%;
    }

    .filter-group select {
        width: 100%;
    }

    .transactions-table {
        display: block;
        overflow-x: auto;
    }
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: #1a1a1a;
    border-radius: 16px;
    width: 95%;
    max-width: 1200px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
    background: linear-gradient(145deg, #222, #2a2a2a);
    padding: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #333;
}

.modal-header h5 {
    font-size: 1.25rem;
    margin: 0;
    color: #fff;
    font-weight: 600;
}

.close-btn {
    background: none;
    border: none;
    color: #fff;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    transition: color 0.2s ease;
}

.close-btn:hover {
    color: #ff69b4;
}

.modal-body {
    padding: 1.5rem;
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 1.5rem;
}

.form-group {
    margin-bottom: 1.5rem;
}

.form-group label {
    display: block;
    color: #fff;
    margin-bottom: 0.75rem;
    font-size: 0.95rem;
    font-weight: 500;
}

.form-control {
    width: 100%;
    padding: 0.875rem 1rem;
    border: 2px solid #333;
    border-radius: 12px;
    background: rgba(26, 26, 26, 0.8);
    color: #fff;
    font-size: 0.95rem;
    transition: all 0.2s ease;
}

.form-control:focus {
    outline: none;
    border-color: #ff69b4;
    box-shadow: 0 0 0 3px rgba(255, 105, 180, 0.1);
}

select.form-control {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 1em;
    padding-right: 2.5rem;
}

textarea.form-control {
    min-height: 120px;
    resize: vertical;
}

.modal-footer {
    padding: 1.5rem;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    border-top: 1px solid #333;
    background: linear-gradient(145deg, #1a1a1a, #222);
}

.btn-pinko {
    padding: 0.875rem 1.5rem;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

.btn-pink {
    background: linear-gradient(145deg, #ff69b4, #ff4da6);
    color: white;
}

.btn-pink:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 105, 180, 0.2);
}

.btn-secondary {
    background: linear-gradient(145deg, #2a2a2a, #333);
    color: #fff;
}

.btn-secondary:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Detail Groups */
.detail-group {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: linear-gradient(145deg, #1a1a1a, #222);
    border-radius: 12px;
    border: 1px solid #333;
}

.detail-group label {
    display: block;
    color: #999;
    margin-bottom: 0.75rem;
    font-size: 0.875rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.detail-group p {
    margin: 0;
    color: #fff;
    font-size: 1rem;
}

.user-detail {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.5rem;
    background: rgba(26, 26, 26, 0.4);
    border-radius: 8px;
}

.user-detail .user-avatar {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    border: 2px solid #333;
}

.text-gray {
    color: #999;
    font-size: 0.875rem;
}

/* Status and Operation Badges in Modal */
.status-badge,
.operation-badge {
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

/* Responsive Design */
@media (max-width: 1024px) {
    .modal-body {
        grid-template-columns: 1fr;
    }

    .detail-section:first-child {
        position: static;
    }
}

@media (max-width: 640px) {
    .modal-content {
        width: 95%;
        margin: 1rem;
        max-width: none;
    }

    .modal-header,
    .modal-body,
.modal-footer {
    padding: 1rem;
    }

    .form-control {
        padding: 0.75rem;
    }

    .btn-pinko {
        padding: 0.75rem 1rem;
    }
}

/* New Modal Detail Styles */
.detail-section {
    margin-bottom: 1.5rem;
    padding: 1.5rem;
    background: linear-gradient(145deg, #1a1a1a, #222);
    border-radius: 12px;
    border: 1px solid #333;
}

.detail-section:first-child {
    position: sticky;
    top: 0;
    align-self: start;
}

.section-title {
    color: #fff;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #333;
}

.badge-container {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.detail-badge {
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
}

.currency-tag {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    margin-left: 0.5rem;
    vertical-align: middle;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    background: rgba(0, 0, 0, 0.2);
    padding: 1rem;
    border-radius: 8px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.info-label {
    color: #999;
    font-size: 0.875rem;
    font-weight: 500;
}

.info-value {
    color: #fff;
    font-size: 0.95rem;
    word-break: break-all;
}

.dates-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
}

.date-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
}

.date-label {
    color: #999;
    font-size: 0.875rem;
}

.date-value {
    color: #fff;
    font-size: 0.95rem;
}

.link-text {
    word-break: break-all;
    color: #4a9eff !important;
    text-decoration: underline;
    cursor: pointer;
}

.invoice-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #4a9eff;
    text-decoration: none;
    padding: 0.5rem 1rem;
    background: rgba(74, 158, 255, 0.1);
    border-radius: 8px;
    transition: all 0.2s ease;
}

.invoice-link:hover {
    background: rgba(74, 158, 255, 0.2);
}

.monospace {
    font-family: monospace;
    font-size: 0.9rem;
    background: rgba(0, 0, 0, 0.2);
    padding: 0.75rem;
    border-radius: 8px;
    overflow-wrap: break-word;
}

.btn-success {
    background: linear-gradient(145deg, #059669, #047857);
    color: white;
}

.btn-success:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.2);
}

.btn-danger {
    background: linear-gradient(145deg, #dc2626, #b91c1c);
    color: white;
}

.btn-danger:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
}

/* Trade Items Styles */
.items-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.75rem;
    margin-top: 0.5rem;
}

.trade-item {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #333;
    transition: transform 0.2s ease;
}

.trade-item:hover {
    transform: translateY(-2px);
}

.item-image-container {
    position: relative;
    width: 100%;
    padding-bottom: 100%;
    background: #000;
}

.item-image-container img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 0.75rem;
}

.game-badge {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    padding: 0.15rem 0.35rem;
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
}

.game-badge.tf2 {
    background: #cf4516;
    color: white;
}

.game-badge.csgo {
    background: #1e72d0;
    color: white;
}

.game-badge.dota2 {
    background: #a52a2a;
    color: white;
}

.item-details {
    padding: 0.75rem;
    border-top: 1px solid #333;
}

.item-name {
    color: #fff;
    font-size: 0.8rem;
    margin: 0 0 0.35rem 0;
    font-weight: 500;
    line-height: 1.2;
}

.item-price {
    color: #4ade80;
    font-weight: 600;
    font-size: 0.85rem;
}

.trade-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #4a9eff;
    text-decoration: none;
    padding: 0.75rem 1rem;
    background: rgba(74, 158, 255, 0.1);
    border-radius: 8px;
    transition: all 0.2s ease;
    font-weight: 500;
}

.trade-link:hover {
    background: rgba(74, 158, 255, 0.2);
    transform: translateY(-1px);
}

.trade-link i {
    font-size: 0.875rem;
}

/* Responsive adjustments */
@media (max-width: 640px) {
    .items-grid {
        grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    }

    .item-name {
    font-size: 0.75rem;
    }

    .item-price {
        font-size: 0.8rem;
    }

    .game-badge {
        font-size: 0.6rem;
        padding: 0.1rem 0.25rem;
    }
}
</style>
